import type { Ceviri } from "../../tip";
import type { deger as tr } from "../../tr/motor/deger";

/** English dictionary · engine messages · deger. */
export const deger: Ceviri<typeof tr> = {
  // --- shared pieces -------------------------------------------------------
  "deger.sebepBilinmiyor": () => "reason unknown",
  "deger.motorSebepSoylemedi": () => "the engine gave no reason",
  "deger.ikiSebep": (p) => `${p.a}; ${p.b}`,
  "deger.parantezListe": (p) => ` (${p.varliklar})`,
  "deger.eksikAralik": (p) => `${p.bas}..${p.son} (${p.sebep})`,
  "deger.payBasina": (p) => ` (${p.tutar} per share)`,
  "deger.bolunmeOrani": (p) => ` (${p.pay}:${p.payda})`,
  "deger.yalnizDegerli": (p) => `; value only: ${p.varliklar}`,
  "deger.yalnizMaliyetli": (p) => `; cost only: ${p.varliklar}`,
  "deger.adMevcutGetiri": () => "Current return",
  "deger.adGerceklesmisGetiri": () => "Realised return",
  "deger.adAcikMaliyet": () => "Open cost",

  // --- FIFO flags ----------------------------------------------------------
  "deger.devirMaliyetiYok": () =>
    "no unit cost was entered for the transferred-in quantity; cost was NOT taken as zero, " +
    "so the gain will be incomplete when this lot is used up",
  "deger.olceklemePartisiz": (p) =>
    `there is a ${p.tur} transaction but no open lot on that date — an older purchase may be missing from the vault`,
  "deger.fazlaSatis": (p) =>
    `${p.miktar} of the quantity sold has no matching lot in the vault — cost was NOT made up; that part has no cost`,
  "deger.karisikParaBirimi": (p) =>
    `this asset has transactions in ${p.pbler}; total cost cannot be given in a single currency`,

  // --- valuation flags -----------------------------------------------------
  "deger.elleFiyatYoksayildi": (p) =>
    `this asset is tracked and its daily close comes from the source; the manual price for ${p.tarih} was NOT USED`,
  "deger.fiyatYokAralik": (p) => `no price at all for this asset on or before ${p.tarih}; missing range: ${p.araliklar}`,
  "deger.fiyatYokOnbellekBos": (p) => `no price at all for this asset on or before ${p.tarih}; cache empty`,
  "deger.paraBirimiCeliskisi": (p) =>
    `the price is in ${p.fiyatPb}, the transactions in ${p.hareketPb}; market value was calculated in the price's currency`,
  "deger.bedelsizRuchanBilinmiyor": () =>
    "no source provides bonus shares or rights issues for this asset; " +
    "quantity and cost rely only on the BONUS/RIGHTS rows in the vault",
  "deger.kaydedilmemisTemettu": (p) =>
    `the source has a dividend with ex-date ${p.tarih}${p.payBasina}; the vault has no DIVIDEND row between ` +
    `${p.bas}..${p.son} — cash flow and return are short by that much`,
  "deger.kaydedilmemisBolunme": (p) =>
    `the source has a split dated ${p.tarih}${p.oran}; the vault has no SPLIT/BONUS row between ` +
    `${p.bas}..${p.son} — quantity, and so market value, may be wrong`,
  "deger.paraBirimiTaninmiyor": (p) =>
    `"${p.kod}" is NOT a recognised currency code; no source can look up its exchange rate, ` +
    "so this asset's cost and gain may not be available. Correct these rows in the vault with a code from the list.",
  "deger.paraBirimiTaninmiyorTahmin": (p) =>
    `"${p.kod}" is NOT a recognised currency code; no source can look up its exchange rate, ` +
    `so this asset's cost and gain may not be available. "${p.tahmin}" was probably meant — correct these rows in the vault.`,
  "deger.kurYokParti": (p) =>
    `no ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih}; this lot's cost could not be converted to the report currency`,
  "deger.kurYokPartiSebep": (p) =>
    `no ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih} — that day's lot cost could not be converted`,
  "deger.kurYokSatis": (p) =>
    `no ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih}; this sale's gain could not be converted to the report currency`,
  "deger.kurYokSatisSebep": (p) =>
    `no ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih} — that day's sale gain could not be converted`,
  "deger.sermayeHaric": (p) =>
    `${p.adet} capital transaction(s) (deposit/withdrawal) are NOT part of the return: ` +
    "putting money into an account is not a gain. Cash balance is a separate question",
  "deger.nakitTutarOkunamadi": (p) =>
    `the ${p.tur} row gives quantity × price of zero but the price is ${p.fiyat}: ` +
    'a cash row should be coded as quantity = "1", price = amount. ' +
    "This row was NOT included in cash flow",
  "deger.kurYokKisa": (p) => `no ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih}`,
  "deger.kurYokNakit": (p) =>
    `no ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih}; this cash transaction could not be converted to the report currency`,
  "deger.satirOkunamadiMiktarsiz": (p) => `the amount of ${p.adet} row(s) could not be read (no quantity)`,
  "deger.kapaliSatisli": () => "no open lots left; only the realised gain is reported",
  "deger.kapaliSatissiz": () =>
    "this asset has NO open lots and no sales — the vault only has transactions without a quantity",
  "deger.elleDegerYokNot": (p) =>
    `no unit price or total value was entered for "${p.etiket}" on or before ${p.tarih}; value was NOT made up`,
  "deger.kurYokDeger": (p) =>
    `no ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih}; the value exists in the asset's currency, NOT in the report currency`,
  "deger.maliyetBilinmeyenParti": () =>
    "the cost of at least one open lot is unknown (transfer in without a price); gain is INCOMPLETE, not taken as zero",
  "deger.kurYokCift": (p) => `no ${p.kaynak}/${p.hedef} exchange rate`,
  "deger.elleDegerGirilmemis": () => "no manual valuation entered",
  "deger.fiyatYok": () => "no price",
  "deger.kovaSatirOkunamadi": (p) =>
    `the amount of ${p.adet} row(s) could not be read (${p.kimlikler}): ` +
    "quantity × price is zero but price is not. This bucket's net cannot be calculated",
  "deger.satirOkunamadi": (p) => `the amount of ${p.adet} row(s) could not be read`,
  "deger.kovaCevrilemedi": (p) =>
    `no ${p.kaynak} → ${p.hedef} exchange rate found for ${p.tarih}. ` +
    "The bucket's net in its own currency is CORRECT and was published",
  "deger.kovaKurYok": (p) => `no ${p.kaynak} → ${p.hedef} exchange rate`,
  "deger.toplamMaliyetEksik": (p) =>
    `cost total INCOMPLETE: ${p.varliklar} could not be converted to the report currency (no exchange rate)`,
  "deger.toplamKazancEksik": (p) =>
    `realised gain total INCOMPLETE: sales of ${p.varliklar} could not be converted to the report currency`,
  "deger.toplamNakitEksik": (p) =>
    `cash flow total INCOMPLETE: cash transactions of ${p.varliklar} could not be converted ` +
    "to the report currency or their amount could not be read",
  "deger.toplamGetiriVerilmedi": () =>
    "realised return TOTAL not given: at least one of its parts " +
    "(realised gain or cash flow) could not be measured for an asset — " +
    "the returns on the position rows are valid",
  "deger.toplamKazancKapsam": (p) =>
    "unrealised gain TOTAL not given: the value and cost totals do not cover " +
    `the same assets${p.yalnizDeger}${p.yalnizMaliyet} — the gains on the position rows are valid`,
  "deger.nakitHareketleriCevrilemedi": (p) =>
    `cash transactions of ${p.varliklar} could not be converted or read`,

  // --- absence reasons: weight ---------------------------------------------
  "deger.payEksiDeger": () => "An asset in scope has a negative value: weight by value cannot be calculated.",
  "deger.payToplamSifir": () => "The values in scope add up to zero: weight cannot be calculated.",
  "deger.payEldeKalmadi": () => "Nothing held: no weight.",
  "deger.payDegerOlculemedi": () =>
    "Weight could not be calculated because the value could not be measured; the other assets' weights are based on the measurable total.",
  "deger.payHesaplanamadi": () => "Weight could not be calculated.",

  // --- absence reasons: lifetime return ------------------------------------
  "deger.bilesenOlculemedi": (p) => `${p.ad}: ${p.sebep}`,
  "deger.getiriHicYok": () => "No holdings, no sales, no dividend/interest/fees: no return.",
  "deger.getiriYokKisa": () => "No return.",
  "deger.satilanMaliyetEksik": () => "Cost of the sold lots is incomplete: no rate given.",
  "deger.cepMaliyetSifir": () => "Money out of pocket is zero: no rate.",

  // --- absence reasons: annualised -----------------------------------------
  "deger.maliyetSifirOranYok": () => "Cost is zero: no rate.",
  "deger.getiriOlculemedi": () => "Return could not be measured.",
  "deger.yasHesaplanamadi": () => "Lot costs could not be converted: age could not be calculated.",
  "deger.yasKisa": (p) => `Weighted age ${p.yas} days: annualising over less than ${p.min} days is misleading.`,
  "deger.yillikHesaplanamadi": () => "Annualised figure could not be calculated.",
  "deger.eldePartiYokYillik": () => "No lots held: the annualised figure is calculated from lots held.",
  "deger.eldePartiYok": () => "No lots held.",
  "deger.maliyetsizPartiOran": () => "There is a lot with unknown cost (transfer in without a price): no rate given.",
  "deger.maliyetsizPartiYas": () => "There is a lot with unknown cost: no weighted age given.",
  "deger.nakitOlculemediOran": () => "Dividend/interest/fees could not be measured: the return would be incomplete.",
  "deger.gerceklesmemisYok": (p) => `No unrealised gain: ${p.sebep}.`,
  "deger.degerYaDaMaliyetEksik": () => "value or cost missing",
  "deger.partiMaliyetiCevrilemedi": () => "Lot costs could not be converted.",
  "deger.eksikToplamYillik": () => "Some positions could not be valued: no annualised figure from an incomplete total.",
  "deger.yasPartiEksik": () => "A lot's cost could not be converted or is unknown: no weighted age given.",
  "deger.partiYokYas": () => "No lots: no weighted age.",

  // --- absence reasons: cash flow ------------------------------------------
  "deger.nakitSatiriDustu": () => "a cash row was dropped",
  "deger.gelirOlculemedi": (p) => `Income could not be measured: ${p.sebep}. The remaining rows were not added up.`,
  "deger.giderOlculemedi": (p) => `Expenses could not be measured: ${p.sebep}. The remaining rows were not added up.`,
  "deger.netNakitOlculemedi": (p) =>
    `Net cash flow could not be measured: ${p.sebep}. The remaining rows were not added up.`,
  "deger.gelirYokVarlik": () => "No dividend or interest income on this asset.",
  "deger.gelirYokKasa": () => "No dividend or interest income in the vault.",
  "deger.giderYokVarlik": () => "No fees on this asset.",
  "deger.giderYokKasa": () => "No fees in the vault.",

  // --- absence reasons: position -------------------------------------------
  "deger.kapaliDegerYok": () => "Closed position: no quantity left, nothing to value.",
  "deger.kapaliFiyatSorulmadi": () => "Closed position: no quantity left, price not asked.",
  "deger.elleToplamDeger": () => "A manual TOTAL value was entered: no unit price, the value comes straight from that entry.",
  "deger.elleHicbiriYok": () => "Neither a manual unit price nor a total value was entered.",
  "deger.fiyatYokKasada": () => "No price: the vault has no price for this asset on or before this date.",
  "deger.degerlenemedi": (p) => `Could not be valued: ${p.sebep}. NOT included in the total.`,
  "deger.acikPartiYok": () => "No open lots: nothing left carrying a cost.",
  "deger.maliyetCevrilemedi": (p) => `Cost could not be converted: ${p.sebep}.`,
  "deger.kapaliKazancYok": () => "Closed position: no unrealised gain — nothing left held.",
  "deger.degerYokKazancSayilmadi": (p) => `No value (${p.sebep}) — gain NOT taken as zero.`,
  "deger.maliyetYokKazancSayilmadi": (p) => `Cost could not be converted (${p.sebep}) — gain NOT taken as zero.`,
  "deger.satisKazanciCevrilemedi": (p) => `There are sales but their gain could not be converted: ${p.sebep}.`,
  "deger.satisYok": () => "No sales: a realised gain only arises from a sale.",
  "deger.nakitAkisiOlculemediSebep": (p) => `Cash flow could not be measured: ${p.sebep}.`,
  "deger.varliktaNakitYok": () => "No dividend, interest or fee transactions on this asset.",
  "deger.kazancOlculemediYarim": () => "Realised gain could not be measured — a half-complete return is not shown.",
  "deger.nakitOlculemediYarim": () => "Cash flow could not be measured — a half-complete return is not shown.",
  "deger.gerceklesmisGetiriYok": () => "No sales and no dividend/interest/fees: no realised return.",

  // --- absence reasons: true return ----------------------------------------
  "deger.nakitIzlenmiyor": () =>
    "Cash is not tracked (Settings › Cash tracking: Off): true return is calculated from cash.",
  "deger.kasadaHareketYok": () => "No transactions in the vault.",
  "deger.asilEksikDeger": () => "Some positions could not be valued: no true return from an incomplete value.",
  "deger.asilTutarOkunamadi": (p) =>
    `The amount of transaction ${p.id} could not be read: true return could not be calculated.`,
  "deger.asilKurYokAkis": (p) =>
    `No ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih}: the money flow could not be converted.`,
  "deger.asilKurYokNakit": (p) => `No ${p.kaynak}/${p.hedef} exchange rate for ${p.tarih}: cash could not be converted.`,
  "deger.netYatirilanSifirEksi": () => "Deposited − withdrawn is zero or negative: no rate.",
  "deger.netYatirilanSifir": () => "Deposited − withdrawn is zero: no rate.",
  "deger.paraYasHesaplanamadi": () => "Money-weighted age could not be calculated.",
  "deger.paraYasKisa": (p) =>
    `Money-weighted age ${p.yas} days: annualising over less than ${p.min} days is misleading.`,
  "deger.paraYasSifir": () => "Money-weighted age is zero.",

  // --- absence reasons: report totals --------------------------------------
  "deger.hicDegerlenemedi": () => "No position could be valued. The excluded ones are listed above with their reasons.",
  "deger.acikPozisyonYok": () => "No open positions: nothing to value.",
  "deger.hicMaliyetCevrilemedi": (p) =>
    `No open lot's cost could be converted to the report currency${p.liste} — no exchange rate.`,
  "deger.acikPozisyonKazancYok": () => "No open positions: no unrealised gain.",
  "deger.kapsamFarkli": () =>
    "The value and cost totals do not cover the same assets — an inconsistent total " +
    "was not published. The gains on the position rows are valid.",
  "deger.hicDegerMaliyetYok": () => "No position has both a value and a cost — gain NOT taken as zero.",
  "deger.hicSatisCevrilemedi": (p) =>
    `None of the sales could be converted to the report currency${p.liste} — no exchange rate.`,
  "deger.nakitOlculemediToplam": (p) =>
    `Cash flow could not be measured${p.liste}: no exchange rate or a row whose amount could not be read.`,
  "deger.kasadaNakitYok": () => "No dividends, interest or fees in the vault.",
  "deger.toplamGetiriBilesenDustu": () =>
    "One of its parts (realised gain or cash flow) could not be measured for an asset " +
    "— a half-complete return is not shown. The returns on the position rows are valid.",
  "deger.kesintiOlculemedi": () =>
    "Transactions carrying withholding information could not be measured: no exchange rate or an unreadable row.",
  "deger.kesintiYok": () => "No sales or dividend/interest transactions: nothing carries withholding information.",
  "deger.nakitTakibiKapali": () => "Cash tracking is off (Settings).",
  "deger.kasadaHicHareketYok": () => "No transactions at all in the vault.",
  "deger.kovaToplamaGiremedi": () =>
    "One of the buckets could not be included — an incomplete total is not published. " +
    "The cash table below shows which one.",

  // --- absence reasons: daily return ---------------------------------------
  "deger.oncekiGetiriOlculemedi": (p) => `Return for ${p.gun} could not be measured: ${p.sebep}`,
  "deger.gunlukDayanakYok": () => "The daily return has no basis.",
  "deger.oncekiDegerOlculemedi": (p) => `Value for ${p.gun} could not be measured: ${p.sebep}`,
  "deger.oncekiGunEldeYok": (p) => `Nothing was held on ${p.gun}: no daily percentage.`,
  "deger.oncekiGunDegerSifir": (p) => `Value was zero on ${p.gun}: no daily percentage.`,
  "deger.eksikToplamGunluk": () => "Some positions could not be valued: no daily return from an incomplete total.",
};
